/**
 * BillPdfDocument.tsx — TGSPDCL HT Bill (VIREON Cortex Estimate)
 * ================================================================
 * Generates a bill PDF that closely mirrors the official TGSPDCL H.T.C.C.
 * printed invoices (Southern Power Distribution Company of Telangana Limited).
 *
 * Visual reference: sample bills SGR2985 / SGR2502 (Jan–Feb 2026).
 * Layout: blue-bordered sections, tabular charge breakdown, amount-in-words,
 * signature block — matching the HT bill format.
 *
 * STATE ROUTING
 * ─────────────
 * BillPdfDownloadButton is the single public entry point.  It reads
 * data.stateCode and dispatches to the correct state-specific renderer:
 *   'telangana'  → BillPdfDocument          (this file — TGSPDCL format)
 *   'west-bengal' → WestBengalBillPdfDocument (WestBengalBillPdfDocument.tsx — WBSEDCL format)
 *
 * Each state renderer is 100 % independent — changes to one do NOT affect
 * the other.  Add new states by creating a new file and registering it in
 * the STATE_PDF_RENDERERS map at the bottom of this file.
 */

import {
  Document, Page, View, Text, StyleSheet, PDFDownloadLink,
} from '@react-pdf/renderer';
import { format, startOfMonth } from 'date-fns';
import {
  DEMAND_CHARGE_NORMAL_RS_KVA, DEMAND_CHARGE_PENAL_RS_KVA,
  ELECTRICITY_DUTY_RS_KVAH, CUSTOMER_CHARGES_RS_MONTH,
  MIN_DEMAND_PCT,
} from '@/config/tariff';
import type { StateCode } from '@/tariffs';
import { WestBengalBillPdfDocument } from './WestBengalBillPdfDocument';

// ─── BillPdfData interface ────────────────────────────────────────────────────

export interface BillPdfData {
  /**
   * State code — drives which PDF renderer is used.
   * Must match a key in the tariff registry (e.g. 'telangana', 'west-bengal').
   * Defaults to 'telangana' for backward compatibility when not supplied.
   */
  stateCode?:         StateCode;

  // Consumer
  consumerName:       string;
  consumerId:         string;
  installationNo:     string;
  serviceAddress:     string;
  meterNo:            string;
  tariffCode:         string;
  supplyVoltageKv:    string;
  contractDemandKva:  number;
  panNo:              string;
  feeder:             string;
  category:           string;
  // Billing period
  billingMonth:       string;
  billingDate:        string;
  /** Opening meter-read date = first day of the BILLING month (e.g. "01-Jun-26").
   *  Derived from the telemetry window, NOT from today. Falls back if omitted. */
  readingFromDate?:   string;
  /** Closing meter-read date = last day of the BILLING month (e.g. "30-Jun-26").
   *  Derived from the telemetry window, NOT from today. Falls back if omitted. */
  readingToDate?:     string;
  invoiceNo:          string;
  payableDate:        string;
  disconnectionDate:  string;
  // Readings & consumption
  readingCurrentKwh:  number;
  readingPreviousKwh: number;
  readingCurrentKvah: number;
  readingPreviousKvah: number;
  differenceKwh:      number;
  differenceKvah:     number;
  multiplyingFactor:  number;
  consumptionKwh:     number;
  consumptionKvah:    number;
  consumptionKva:     number;
  tod1Kvah:           number;  // Peak period kVAh (TGSPDCL: MTD Peak kVAh; WB: per-period kWh)
  tod2Kvah:           number;  // Off-Peak kVAh (TGSPDCL: MTD Off-Peak kVAh; WB: per-period kWh)
  // ── Telangana ToD billing model (new) ─────────────────────────────────────
  /** TGSPDCL: Base Energy Charge = totalKvah × Normal rate */
  bkEcBaseRs?:          number;
  /** TGSPDCL: MTD Peak kVAh (for ToD Peak Penalty column) */
  bkTodPeakKvah?:       number;
  /** TGSPDCL: MTD Off-Peak kVAh (for ToD Off-Peak Rebate column) */
  bkTodOffpeakKvah?:    number;
  /** TGSPDCL: ToD Peak Penalty = peakKvah × (peakRate − normalRate). Positive = adds to bill. */
  bkTodPeakPenaltyRs?:  number;
  /** TGSPDCL: ToD Off-Peak Rebate = offpeakKvah × (normalRate − offpeakRate). Positive = saving. */
  bkTodOffpeakRebateRs?: number;
  // Compatibility aliases used by the billing page
  kwhNormal?:         number;
  kwhPeak?:           number;
  kwhOffpeak?:        number;
  // Demand
  maxDemandKva:       number;
  normalKva:          number;
  addlKva:            number;
  maxDemandKvaNormal?: number;
  maxDemandKvaPeak?:   number;
  maxDemandKvaOffpeak?: number;
  chargeableKva?:      number;
  demandChargeRs?:     number;
  mdOverrunKva?:       number;
  mdOverrunPenaltyRs?: number;
  // Energy charges
  energyChargeNormal:  number;
  energyChargePeak:    number;
  energyChargeOffpeak: number;
  totalEnergyCharge:   number;
  rateNormal:          number;  // Rs/kVAh
  ratePeak:            number;
  rateOffpeak:         number;
  /**
   * Billing-engine per-period energy charges (NOT Pi accumulator).
   * These always match what the dashboard ToD chart displays.
   * Preferred over energyChargeNormal/Peak/Offpeak when non-zero.
   */
  bkEcNormal?:          number;
  bkEcPeak?:            number;
  bkEcOffpeak?:         number;
  /** Billing-engine total kVAh for TGSPDCL (not Pi accumulator). */
  bkTotalKvah?:         number;
  /**
   * Projected per-period kVAh from the billing engine.
   * Used in the OPTIONS/RATE table so Energy Charges and TOD Charges
   * show projected full-month units, matching the dashboard Projected Bill card.
   */
  projectedKvahNormal?:  number;
  projectedKvahPeak?:    number;
  projectedKvahOffpeak?: number;
  pfRebatePct?:        number;
  pfRebatePctNormal?:  number;
  pfRebatePctPeak?:    number;
  pfRebatePctOffpeak?: number;
  pfRebateCost?:       number;
  lfPct?:              number;
  lfRebateCost?:       number;
  // TOD charge (peak surcharge line)
  todChargeRs:        number;
  todKvah:            number;
  // Duty & fixed
  electricityDutyRs:  number;
  meterRentalRs?:     number;
  colonyChargesRs:    number;
  lfChargesRs:        number;
  // Totals
  subTotalRs:         number;
  customerChargesRs:  number;
  acdSurchargeRs:     number;
  uiChargesRs:        number;
  crossSubsidyRs:     number;
  addlSurchargeRs:    number;
  latePaymentRs:      number;
  interestOnEdRs:     number;
  incTod1Rs:          number;
  incTod2Rs:          number;
  wheelingChargesRs:  number;
  transmissionRs:     number;
  gridSupportRs:      number;
  otherCharges1Rs:    number;
  otherCharges2Rs:    number;
  grossTotalRs:       number;
  otherCreditAdjRs:   number;
  netBillAmountRs:    number;
  totalArrearsRs:     number;
  totalAmountPayableRs: number;
  grossBeforeEd?:     number;
  grossPayable?:      number;
  grossPayableRs?:    number;
  netPayable?:        number;
  tprRs?:             number;
  projectedGrossEcRs?: number;
  projectedPfAdjRs?:   number;
  projectedLfAdjRs?:   number;
  projectedEdRs?:      number;
  projectedTprRs?:     number;
  // Projection
  projectedNetPayable: number;
  daysPassed:         number;
  daysInMonth:        number;
  peakDemandTimestamp?: string;
  // PF
  pfAvg:              number;

  // ── West Bengal (WBSEDCL) specific fields ────────────────────────────────
  // These are ignored by the Telangana renderer; used only by WestBengalBillPdfDocument.

  /** Previous reading date string (first day of billing month, e.g. "01-Jun-2026").
   *  WB only — Telangana ignores this field. */
  wbPreviousReadingDate?: string;

  /** Per-period average PF for "This Month" — from billing engine breakdown.
   *  WB per-period PF slabs require the individual-period PF, not just the overall average.
   *  Telangana ignores these fields. */
  wbPfAvgNormal?:   number;
  wbPfAvgPeak?:     number;
  wbPfAvgOffpeak?:  number;

  /** Per-period MTD kWh from the billing engine breakdown (= "This Month" values on dashboard).
   *  These are the authoritative kWh values — same source as the dashboard Energy Charges card.
   *  WB only. Billings.tsx passes bkKwhNormal/Peak/Offpeak directly.
   *  Telangana ignores these fields. */
  wbKwhNormal?:     number;
  wbKwhPeak?:       number;
  wbKwhOffpeak?:    number;

  /** Per-period kWh at the START of the billing month (billingRows[0] first-row values).
   *  Used as "Previous Reading" in the meter table.  Present − Previous = Reading Advance.
   *  WB only. Telangana ignores these fields. */
  wbPrevKwhNormal?:    number;
  wbPrevKwhPeak?:      number;
  wbPrevKwhOffpeak?:   number;

  /** Per-period max demand kVA from the billing engine (breakdown.mdKvaNormal/Peak/Offpeak).
   *  Overrides d.maxDemandKvaNormal/Peak/Offpeak which come from a Pi accumulator.
   *  WB only. Telangana ignores these fields. */
  wbMdKvaNormal?:   number;
  wbMdKvaPeak?:     number;
  wbMdKvaOffpeak?:  number;
}

// ─── Styles — blue-bordered, matching TGSPDCL HT bill ────────────────────────

const BLUE   = '#1565C0';
const DKBLUE = '#0D47A1';
const BLACK  = '#000000';
const WHITE  = '#FFFFFF';

const S = StyleSheet.create({
  page: {
    fontFamily: 'Courier',
    fontSize: 6.5,
    color: BLACK,
    paddingHorizontal: 22,
    paddingVertical: 18,
    backgroundColor: WHITE,
  },

  // ── Main page border
  pageBorder: {
    position: 'absolute',
    top: 8, left: 8, right: 8, bottom: 8,
    borderWidth: 1, borderColor: BLUE,
  },

  // ── Header
  headerBox: {
    borderWidth: 1.5, borderColor: BLUE,
    marginBottom: 3, padding: 4,
    alignItems: 'center',
  },
  hCompany: {
    fontFamily: 'Courier-Bold', fontSize: 9.5, color: DKBLUE,
    textAlign: 'center', marginBottom: 1,
  },
  hSub: {
    fontFamily: 'Courier-Bold', fontSize: 7.5, color: BLUE,
    textAlign: 'center', marginBottom: 2,
  },
  hMonthRow: {
    flexDirection: 'row', width: '100%', justifyContent: 'space-between',
  },
  hMonthText: { fontFamily: 'Courier', fontSize: 6.5, color: BLACK },
  hBold: { fontFamily: 'Courier-Bold', fontSize: 6.5, color: BLACK },
  hEstimate: {
    fontFamily: 'Courier-BoldOblique', fontSize: 5.5, color: BLUE,
    textAlign: 'center', marginTop: 2,
  },

  // ── 2-col info row
  infoRow2: {
    flexDirection: 'row', borderWidth: 1, borderColor: BLUE,
    marginBottom: 3,
  },
  infoLeft: { flex: 1, borderRightWidth: 1, borderRightColor: BLUE, padding: 4 },
  infoRight: { flex: 1, padding: 4 },
  iRow: { flexDirection: 'row', marginBottom: 1.5 },
  iLbl: { fontFamily: 'Courier', fontSize: 6, color: BLACK, width: 80 },
  iVal: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, flex: 1 },
  iName: { fontFamily: 'Courier-Bold', fontSize: 7, color: BLACK, marginBottom: 1.5 },
  iAddr: { fontFamily: 'Courier', fontSize: 6, color: BLACK, lineHeight: 1.5 },

  // ── Section header (blue bg)
  sHdr: {
    backgroundColor: BLUE, paddingHorizontal: 5, paddingVertical: 2.5,
  },
  sHdrText: { fontFamily: 'Courier-Bold', fontSize: 6.5, color: WHITE },

  // ── DESCRIPTIONS table
  descTable: { borderWidth: 1, borderColor: BLUE, marginBottom: 3 },
  descHdrRow: {
    flexDirection: 'row', backgroundColor: BLUE,
    paddingVertical: 2, paddingHorizontal: 4,
  },
  descHdrCell: {
    fontFamily: 'Courier-Bold', fontSize: 6, color: WHITE,
    textAlign: 'center',
  },
  descRow: {
    flexDirection: 'row',
    borderTopWidth: 0.5, borderTopColor: BLUE,
    paddingVertical: 1.5, paddingHorizontal: 4,
  },
  dLabel: { fontFamily: 'Courier',      fontSize: 6, color: BLACK, width: 95 },
  dLabelB: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, width: 95 },
  dCell: {
    fontFamily: 'Courier', fontSize: 6, color: BLACK,
    flex: 1, textAlign: 'right',
  },
  dCellB: {
    fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK,
    flex: 1, textAlign: 'right',
  },

  // ── Charges table (rate + amounts)
  chargesTable: { borderWidth: 1, borderColor: BLUE, marginBottom: 3 },
  chHdr: {
    flexDirection: 'row', backgroundColor: BLUE,
    paddingVertical: 2, paddingHorizontal: 4,
  },
  chRow: {
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: BLUE,
    paddingVertical: 1.5, paddingHorizontal: 4,
  },
  chRowBlue: {
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: BLUE,
    paddingVertical: 1.5, paddingHorizontal: 4,
    backgroundColor: '#EEF3FF',
  },
  chDesc:  { fontFamily: 'Courier', fontSize: 6, color: BLACK, width: 130 },
  chDescB: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, width: 130 },
  chRate:  { fontFamily: 'Courier', fontSize: 6, color: BLACK, width: 65, textAlign: 'center' },
  chKva:   { fontFamily: 'Courier', fontSize: 6, color: BLACK, width: 70, textAlign: 'right' },
  chAmt:   { fontFamily: 'Courier', fontSize: 6, color: BLACK, flex: 1, textAlign: 'right' },
  chAmtB:  { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, flex: 1, textAlign: 'right' },
  chHdrCell: { fontFamily: 'Courier-Bold', fontSize: 6, color: WHITE },

  // ── 2-column lower section (amounts right)
  twoCol: {
    flexDirection: 'row', borderWidth: 1, borderColor: BLUE,
    marginBottom: 3,
  },
  leftPane: { flex: 2.2, borderRightWidth: 1, borderRightColor: BLUE },
  rightPane: { flex: 1.6 },

  // ── Arrears block
  arrearBlock: {
    borderTopWidth: 0.5, borderTopColor: BLUE,
    paddingVertical: 2, paddingHorizontal: 5,
  },
  arrearText: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, lineHeight: 1.6 },

  // ── Amount rows (right pane)
  amtRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    paddingHorizontal: 5, paddingVertical: 1.5,
    borderBottomWidth: 0.3, borderBottomColor: BLUE,
  },
  amtRowB: {
    flexDirection: 'row', justifyContent: 'space-between',
    paddingHorizontal: 5, paddingVertical: 2,
    backgroundColor: '#E3F2FD',
    borderBottomWidth: 0.5, borderBottomColor: BLUE,
  },
  amtLbl:  { fontFamily: 'Courier',      fontSize: 5.8, color: BLACK, flex: 1 },
  amtVal:  { fontFamily: 'Courier',      fontSize: 5.8, color: BLACK, textAlign: 'right' },
  amtValB: { fontFamily: 'Courier-Bold', fontSize: 6,   color: DKBLUE, textAlign: 'right' },

  // ── Net payable total box
  netBox: {
    borderWidth: 1.5, borderColor: BLUE, marginBottom: 4,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 8, paddingVertical: 6,
    backgroundColor: '#E3F2FD',
  },
  netLbl:  { fontFamily: 'Courier-Bold', fontSize: 8, color: DKBLUE },
  netAmt:  { fontFamily: 'Courier-Bold', fontSize: 16, color: DKBLUE },
  netSub:  { fontFamily: 'Courier', fontSize: 5.5, color: BLUE, marginTop: 1 },

  // ── Words box
  wordsBox: {
    borderWidth: 1, borderColor: BLUE, marginBottom: 4,
    paddingHorizontal: 6, paddingVertical: 3,
    flexDirection: 'row',
  },
  wordsLbl: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLUE, width: 110 },
  wordsVal: { fontFamily: 'Courier', fontSize: 6, color: BLACK, flex: 1, lineHeight: 1.5 },

  // ── Payment note
  noteBox: {
    borderWidth: 1, borderColor: BLUE, padding: 5, marginBottom: 4,
  },
  noteLine: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, lineHeight: 1.7, marginBottom: 1 },
  noteBold: { fontFamily: 'Courier-Bold', fontSize: 5.5, color: BLACK },

  // ── Signature / footer row
  sigRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'flex-end', marginTop: 2,
  },
  sigLeft: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, flex: 1 },
  sigRight: {
    borderWidth: 1, borderColor: BLUE, padding: 6, width: 120, alignItems: 'center',
  },
  sigLine: {
    borderTopWidth: 0.5, borderTopColor: BLACK, width: 80, marginBottom: 2, marginTop: 10,
  },
  sigText: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, textAlign: 'center' },
  sigSub:  { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, textAlign: 'center' },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function rs(v: number): string {
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fn(v: number, d = 2): string { return v.toFixed(d); }

function numberToWords(n: number): string {
  if (n <= 0) return 'Zero Only';
  const ones  = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven',
                  'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen',
                  'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens  = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty',
                  'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function chunk(val: number): string {
    if (!val) return '';
    if (val < 20) return ones[val] + ' ';
    if (val < 100) return tens[Math.floor(val / 10)] + (ones[val % 10] ? ' ' + ones[val % 10] + ' ' : ' ');
    return ones[Math.floor(val / 100)] + ' Hundred ' + chunk(val % 100);
  }
  const cr   = Math.floor(n / 10_000_000);
  const lakh = Math.floor((n % 10_000_000) / 100_000);
  const thou = Math.floor((n % 100_000) / 1_000);
  const rest = Math.floor(n % 1_000);
  let out = '';
  if (cr)   out += chunk(cr)   + 'Crore ';
  if (lakh) out += chunk(lakh) + 'Lakh ';
  if (thou) out += chunk(thou) + 'Thousand ';
  if (rest) out += chunk(rest);
  return out.trim().replace(/\s+/g, ' ') + ' Only';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function IRow({ lbl, val }: { lbl: string; val: string }) {
  return (
    <View style={S.iRow}>
      <Text style={S.iLbl}>{lbl}</Text>
      <Text style={S.iVal}>{val}</Text>
    </View>
  );
}

function DescRow({
  lbl, kwh, kvah, kva, todPeak, todOffpeak, bold,
}: {
  lbl: string; kwh: string; kvah: string; kva: string; todPeak: string; todOffpeak: string; bold?: boolean;
}) {
  return (
    <View style={S.descRow}>
      <Text style={bold ? S.dLabelB : S.dLabel}>{lbl}</Text>
      <Text style={bold ? S.dCellB : S.dCell}>{kwh}</Text>
      <Text style={bold ? S.dCellB : S.dCell}>{kvah}</Text>
      <Text style={bold ? S.dCellB : S.dCell}>{kva}</Text>
      <Text style={bold ? S.dCellB : S.dCell}>{todPeak}</Text>
      <Text style={bold ? S.dCellB : S.dCell}>{todOffpeak}</Text>
    </View>
  );
}

function ChRow({
  desc, rate, kvaUnits, amount, bold, blueRow,
}: {
  desc: string; rate: string; kvaUnits: string; amount: string;
  bold?: boolean; blueRow?: boolean;
}) {
  return (
    <View style={blueRow ? S.chRowBlue : S.chRow}>
      <Text style={bold ? S.chDescB : S.chDesc}>{desc}</Text>
      <Text style={S.chRate}>{rate}</Text>
      <Text style={S.chKva}>{kvaUnits}</Text>
      <Text style={bold ? S.chAmtB : S.chAmt}>{amount}</Text>
    </View>
  );
}

function AmtRow({ lbl, val, bold }: { lbl: string; val: string; bold?: boolean }) {
  return (
    <View style={bold ? S.amtRowB : S.amtRow}>
      <Text style={S.amtLbl}>{lbl}</Text>
      <Text style={bold ? S.amtValB : S.amtVal}>{val}</Text>
    </View>
  );
}

// ─── Main Document ────────────────────────────────────────────────────────────

export function BillPdfDocument({ d }: { d: BillPdfData }) {
  const genDate     = format(new Date(), 'dd-MMM-yy');
  const genDateTime = format(new Date(), 'dd MMM yyyy, hh:mm a');
  const cycleStart  = format(startOfMonth(new Date()), 'dd-MMM-yy');

  // ── Reading-row dates ───────────────────────────────────────────────────────
  // These label the OPENING and CLOSING meter reads and MUST come from the
  // billing period the data covers — NOT from new Date() (the generation moment).
  // Using now() is what produced "Reading On 01-Jul / 04-Jul" on a JUNE bill.
  // genDate/genDateTime/cycleStart above stay only for the "Generated:" footer.
  const readingToDate   = d.readingToDate   ?? d.billingDate ?? genDate;   // closing = last day of billing month (e.g. 30-Jun-26)
  const readingFromDate = d.readingFromDate ?? cycleStart;                 // opening = first day of billing month (e.g. 01-Jun-26)

  // ── Demand calculations ──────────────────────────────────────────────────────
  // Use billing engine breakdown values (passed from Billings.tsx) as the
  // authoritative source. These are the same values the dashboard displays.
  // chargeableKva = breakdown.normalKva (rounded, engine-computed)
  // addlKva       = breakdown.addlKva   (rounded, engine-computed)
  // demandChargeRs = chargeableKva × ₹500/kVA (matches dashboard "Normal demand charge")
  // mdOverrunPenaltyRs = addlKva × ₹1000/kVA (matches dashboard "Additional demand charge")
  const normalKva: number = (() => {
    if (d.chargeableKva && d.chargeableKva > 0) return d.chargeableKva;
    if (d.normalKva && d.normalKva > 0) return d.normalKva;
    if (d.demandChargeRs && d.demandChargeRs > 0)
      return Math.round(d.demandChargeRs / DEMAND_CHARGE_NORMAL_RS_KVA);
    return Math.round(Math.max(d.maxDemandKva || 0, d.contractDemandKva * MIN_DEMAND_PCT));
  })();

  const addlKva: number = (() => {
    if (d.mdOverrunKva !== undefined && d.mdOverrunKva !== null) return d.mdOverrunKva;
    if (d.addlKva && d.addlKva > 0) return d.addlKva;
    return Math.round(Math.max(0, (d.maxDemandKva || 0) - d.contractDemandKva));
  })();

  // Demand charges — use pre-computed values from dashboard (already match billing engine)
  const dcNormal: number = d.demandChargeRs && d.demandChargeRs > 0
    ? d.demandChargeRs
    : normalKva * DEMAND_CHARGE_NORMAL_RS_KVA;
  const dcPenal: number = d.mdOverrunPenaltyRs !== undefined && d.mdOverrunPenaltyRs !== null
    ? d.mdOverrunPenaltyRs
    : addlKva * DEMAND_CHARGE_PENAL_RS_KVA;

  // ── Energy charge rates ─────────────────────────────────────────────────────
  // TGSPDCL HT tier-1: Normal 7.15 / Peak 8.65 / Off-Peak 6.65 Rs/kVAh
  const rateNormal  = d.rateNormal  > 0 ? d.rateNormal  : 7.15;
  const ratePeak    = d.ratePeak    > 0 ? d.ratePeak    : 8.65;
  const rateOffpeak = d.rateOffpeak > 0 ? d.rateOffpeak : 6.65;

  const daysPassed  = d.daysPassed  || 1;
  const daysInMonth = d.daysInMonth || 30;

  // ── TGSPDCL Billing Model: Base EC + ToD Peak Penalty − ToD Off-Peak Rebate ─
  // Source: dashboard billing engine (billing.ts buildBillBreakdown).
  // All values passed from Billings.tsx via bkTod* / bkEcBase* props.
  //
  // MTD values (actuals to date, NOT projected):
  const mtdPeakKvah      = d.bkTodPeakKvah       ?? d.tod1Kvah ?? 0;  // cumulative Peak kVAh MTD
  const mtdOffpeakKvah   = d.bkTodOffpeakKvah     ?? d.tod2Kvah ?? 0;  // cumulative Off-Peak kVAh MTD
  const mtdTotalKvah     = d.bkTotalKvah ?? d.consumptionKvah ?? 0;    // cumulative Total kVAh MTD

  // ── Projected (full-month) values ───────────────────────────────────────────
  // Projected per-period kVAh from the billing engine.
  // Used in the PROJECTED columns of the rate table.
  const mtdNormalKvah = mtdTotalKvah - mtdPeakKvah - mtdOffpeakKvah;

  const projKvahNormal  = (d.projectedKvahNormal  != null && d.projectedKvahNormal  > 0)
    ? d.projectedKvahNormal
    : (mtdNormalKvah  / daysPassed) * daysInMonth;
  const projKvahPeak    = (d.projectedKvahPeak    != null && d.projectedKvahPeak    > 0)
    ? d.projectedKvahPeak
    : (mtdPeakKvah    / daysPassed) * daysInMonth;
  const projKvahOffpeak = (d.projectedKvahOffpeak != null && d.projectedKvahOffpeak > 0)
    ? d.projectedKvahOffpeak
    : (mtdOffpeakKvah / daysPassed) * daysInMonth;

  // Projected total kVAh = sum of all periods
  const projTotalKvah = projKvahNormal + projKvahPeak + projKvahOffpeak;

  // Projected TGSPDCL billing model (same formula, projected quantities):
  const projEcBase        = projTotalKvah * rateNormal;
  const projPeakPenalty   = projKvahPeak    * (ratePeak    - rateNormal);  // penalty (positive)
  const projOffpeakRebate = projKvahOffpeak * (rateNormal  - rateOffpeak); // rebate (positive = saving)

  // Projected gross energy charge — prefer the billing-engine value (matches dashboard "Projected Bill" card).
  const projTotalEnergyCharge = (d.projectedGrossEcRs != null && d.projectedGrossEcRs > 0)
    ? d.projectedGrossEcRs
    : (projEcBase + projPeakPenalty - projOffpeakRebate);

  // ── Electricity Duty ─────────────────────────────────────────────────────────
  // For the PROJECTED charges table: ED = 6 paise/kVAh on projected full-month kVAh.
  const electricityDutyRsProj = (d.projectedEdRs != null && d.projectedEdRs > 0)
    ? d.projectedEdRs
    : projTotalKvah * ELECTRICITY_DUTY_RS_KVAH;

  // edKvah = projected kVAh that produces this ED amount (for the rate table column)
  const edKvah = ELECTRICITY_DUTY_RS_KVAH > 0
    ? electricityDutyRsProj / ELECTRICITY_DUTY_RS_KVAH
    : projTotalKvah;

  // ── Customer charges (₹3,500/month fixed) ───────────────────────────────────
  const customerChargesRs = d.customerChargesRs > 0
    ? d.customerChargesRs
    : CUSTOMER_CHARGES_RS_MONTH;

  // ACD (Additional Consumption Deposit) surcharge — per-consumer, from account
  // record. Shown as its own summary line and folded into Gross Total.
  const acdSurchargeRs = d.acdSurchargeRs || 0;

  // ── Amount Summary values — use MTD actuals from dashboard (d.subTotalRs, d.grossTotalRs) ──
  // These are pre-computed by Billings.tsx using the same billing engine as the dashboard,
  // so Sub Total and Gross Total in the PDF exactly match what the dashboard displays.
  //
  // TGSPDCL formula (spec):
  //   Sub Total  = Demand Normal + Demand Penal + Base EC + ToD Peak Penalty
  //                − ToD Off-Peak Rebate + Electricity Duty + Colony + L&F
  //                + Green Energy + FSA + (all charges EXCEPT Customer Charges)
  //   Gross Total = Sub Total + Customer Charges   ← Customer added ONLY here
  //
  // We trust the dashboard values passed via props; the fallback recalculates
  // from MTD components using the same formula to guarantee consistency.
  const colonyChargesRs_pdf  = d.colonyChargesRs  || 0;
  const lfChargesRs_pdf      = d.lfChargesRs      || 0;

  // ── Reading section — all PROJECTED full-month values ────────────────────
  // This is a projected month-end estimate, so readings table shows projected
  // end-of-month consumption, not the MTD register snapshot.
  //   Previous Reading = 0  (billing month resets each cycle)
  //   Present Reading  = projected full-month kVAh / kWh
  //   Difference       = Present − Previous = projected full-month consumption
  //
  // kWh ≈ kVAh × pfAvg (TGSPDCL bills on kVAh; kWh is derived for the table)
  const pfAvg = d.pfAvg > 0 && d.pfAvg <= 1 ? d.pfAvg : 0.95;
  const projTotalKwh   = projTotalKvah * pfAvg;

  // Present Reading = projected full-month
  const currentKwh  = projTotalKwh;
  const currentKvah = projTotalKvah;   // kVAh is the TGSPDCL billing unit
  // Previous Reading = 0 (register resets each billing month)
  const previousKwh  = 0;
  const previousKvah = 0;
  // Difference = Present − Previous = projected full-month consumption
  const diffKwh  = projTotalKwh;
  const diffKvah = projTotalKvah;

  // ── Amount Summary — projected values to match dashboard Projected Bill card ─
  const projSubTotalRs  = projTotalEnergyCharge + dcNormal + dcPenal + electricityDutyRsProj
    + colonyChargesRs_pdf + lfChargesRs_pdf;
  const projGrossTotalRs = projSubTotalRs + customerChargesRs + acdSurchargeRs;

  // ── Net payable — single source of truth ────────────────────────────────────
  const projectedPayable     = d.projectedNetPayable;
  const netBillAmountRs      = projectedPayable;
  const totalAmountPayableRs = projectedPayable;

  return (
    <Document
      title={`CORTEX Bill Estimate — ${d.billingMonth}`}
      author="VIREON Cortex Energy Monitor"
      subject="TGSPDCL HT Bill Estimate (Projected Month-End)"
    >
      <Page size="A4" style={S.page}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <View style={S.headerBox}>
          <Text style={S.hCompany}>SOUTHERN POWER DISTRIBUTION COMPANY OF TELANGANA LIMITED</Text>
          <Text style={S.hSub}>H.T.C.C. Bill for the month of   {d.billingMonth}</Text>
          <View style={S.hMonthRow}>
            <Text style={S.hMonthText}>
              Dated  <Text style={S.hBold}>{d.billingDate || '01-' + genDate.slice(3)}</Text>
            </Text>
          </View>
          <Text style={S.hEstimate}>
            * SYSTEM-GENERATED PROJECTED MONTH-END ESTIMATE — NOT AN OFFICIAL TGSPDCL INVOICE *
          </Text>
        </View>

        {/* ── Consumer Info ───────────────────────────────────────────── */}
        <View style={S.infoRow2}>
          <View style={S.infoLeft}>
            <IRow lbl="Contracted MD (KVA/HP)" val={String(d.contractDemandKva)} />
            <IRow lbl="Specified Voltage (KV)"  val={d.supplyVoltageKv || '33'}  />
            <IRow lbl="Actual Voltage (KV)"     val={d.supplyVoltageKv || '33'}  />
            <IRow lbl="Feeder"                  val={d.feeder || '—'}            />
            <IRow lbl="Category"                val={d.category || '1A'}         />
          </View>
          <View style={S.infoRight}>
            <Text style={S.iName}>{d.consumerId}</Text>
            <Text style={S.iName}>{d.consumerName}</Text>
            <Text style={S.iAddr}>{d.installationNo}</Text>
            <Text style={S.iAddr}>{d.serviceAddress}</Text>
          </View>
        </View>

        {/* ── DESCRIPTIONS table ──────────────────────────────────────── */}
        {/* Column headers: KWH = active energy (kWh), KVAH = apparent energy (kVAh)          */}
        {/* ToD columns: 0 on register-reading rows; MTD period kVAh on Consumption row only   */}
        {/*                                                                                      */}
        {/* IMPORTANT — unit notes for TGSPDCL:                                                */}
        {/*   KWH column  = accumulated active energy in kWh (whReceived ÷ 1000).             */}
        {/*                 Meter register is raw Wh; Billings.tsx passes energyKwh = Wh÷1000.*/}
        {/*   KVAH column = accumulated apparent energy in kVAh (vahReceived ÷ 1000).         */}
        {/*                 Meter register is raw VAh; Billings.tsx passes energyVAh = raw VAh.*/}
        {/*                 We divide by 1000 here so the PDF shows kVAh, not VAh.            */}
        {/*   KVA column  = billing-cycle peak demand in kVA (max 15-min block avg).          */}
        {/*   ToD columns = MTD kVAh for each ToD window (from billing engine, not registers). */}
        <View style={S.descTable}>
          <View style={S.descHdrRow}>
            <Text style={[S.descHdrCell, { width: 95 }]}>DESCRIPTIONS</Text>
            <Text style={[S.descHdrCell, { flex: 1 }]}>KWH</Text>
            <Text style={[S.descHdrCell, { flex: 1 }]}>KVAH</Text>
            <Text style={[S.descHdrCell, { flex: 1 }]}>KVA</Text>
            <Text style={[S.descHdrCell, { flex: 1 }]}>ToD (Peak{'\n'}Penalty)</Text>
            <Text style={[S.descHdrCell, { flex: 1 }]}>ToD (Off-Peak{'\n'}Rebate)</Text>
          </View>

          {/* ── Current (projected month-end) reading row ────────────────
              KWH : projected total kWh (kVAh × pfAvg)
              KVAH: projected total kVAh (TGSPDCL billing unit)
              KVA : actual measured peak demand kVA (d.maxDemandKva — the running
                    billing-cycle maximum, same value the dashboard "Billing Cycle Peak" shows)
              ToD : blank on reading rows */}
          <DescRow lbl={`Reading On  ${readingToDate}`}
            kwh={fn(currentKwh, 2)}
            kvah={fn(currentKvah, 2)}
            kva={fn(d.maxDemandKva || 0, 2)}
            todPeak=""
            todOffpeak="" />

          {/* ── Previous reading = 0 (billing month resets each cycle) ── */}
          <DescRow lbl={`Reading On  ${readingFromDate}`}
            kwh={fn(previousKwh, 2)}
            kvah={fn(previousKvah, 2)}
            kva="" todPeak="" todOffpeak="" />

          {/* ── Difference = Present − Previous = projected full-month ──
              KVA column is blank here — there is no meaningful kVA "delta"
              between readings; the peak demand is shown on the reading rows. */}
          <DescRow lbl="Difference  ST:01"
            kwh={fn(diffKwh, 2)}
            kvah={fn(diffKvah, 2)}
            kva=""
            todPeak=""
            todOffpeak="" />

          {/* ── Multiplying Factor — KVA column blank (MF is a pure scalar) ── */}
          <DescRow lbl="Multiplying Factor"
            kwh={fn(d.multiplyingFactor || 1, 2)} kvah={fn(d.multiplyingFactor || 1, 2)}
            kva="" todPeak={fn(d.multiplyingFactor || 1, 2)}
            todOffpeak={fn(d.multiplyingFactor || 1, 2)} />

          {/* ── Consumption (Projected Month-End) ────────────────────────
              KWH : projected total kWh
              KVAH: projected total kVAh
              KVA : actual measured peak demand kVA (same as Reading row)
              ToD : projected Peak and Off-Peak kVAh */}
          <DescRow lbl="   Consumption (Projected Month-End)"
            kwh={fn(diffKwh, 2)}
            kvah={fn(projTotalKvah, 2)}
            kva={fn(d.maxDemandKva || 0, 2)}
            todPeak={fn(projKvahPeak, 2)}
            todOffpeak={fn(projKvahOffpeak, 2)}
            bold />

          {/* ── Monthly Minimum Units ─────────────────────────────────────
              Per spec: display value ONLY in the KVA column. */}
          <DescRow lbl="   Monthly Minimum Units"
            kwh="" kvah="" kva={fn(d.contractDemandKva * MIN_DEMAND_PCT, 2)}
            todPeak="" todOffpeak="" />

          {/* Main Consumption: projected total kVAh (TGSPDCL billing unit) */}
          <DescRow lbl="   Main Consumption (Projected)"
            kwh={fn(projTotalKvah, 2)} kvah="" kva="" todPeak="" todOffpeak="" bold />

          {/* Demand + misc rows */}
          <View style={S.descRow}>
            <Text style={S.dLabel}>{'   Demand Normal'}</Text>
            <Text style={S.dCell}>{fn(normalKva, 2)}</Text>
            <Text style={S.dCell}>Colony</Text>
            <Text style={S.dCell}>{''}</Text>
            <Text style={S.dCell}>L&F</Text>
            <Text style={S.dCell}>{fn(d.lfChargesRs || 0, 2)}</Text>
          </View>
          <View style={S.descRow}>
            <Text style={S.dLabel}>{'   Demand Penal'}</Text>
            <Text style={S.dCell}>{fn(addlKva, 2)}</Text>
            <Text style={S.dCell}>Inc. ToD Off-Peak</Text>
            <Text style={S.dCell}>{''}</Text>
            <Text style={S.dCell}>{fn(d.incTod2Rs || 0, 2)}</Text>
            <Text style={S.dCell}>{''}</Text>
          </View>
        </View>

        {/* ── Charges + Amounts (2-column) ────────────────────────────── */}
        <View style={S.twoCol}>

          {/* LEFT: rate table — all quantities are PROJECTED full-month */}
          <View style={S.leftPane}>
            <View style={S.sHdr}>
              <Text style={S.sHdrText}>OPTIONS / RATE</Text>
            </View>
            <View style={S.chHdr}>
              <Text style={[S.chHdrCell, { width: 130 }]}>DESCRIPTIONS</Text>
              <Text style={[S.chHdrCell, { width: 65, textAlign: 'center' }]}>RATE</Text>
              <Text style={[S.chHdrCell, { width: 70, textAlign: 'right' }]}>KVA / UNITS</Text>
              <Text style={[S.chHdrCell, { flex: 1, textAlign: 'right' }]}>AMOUNT Rs.</Text>
            </View>

            {/* Demand Charges Normal — billing-cycle peak × ₹500/kVA */}
            <ChRow
              desc="Demand Charges Normal"
              rate={`Rs ${DEMAND_CHARGE_NORMAL_RS_KVA}`}
              kvaUnits={fn(normalKva, 1)}
              amount={rs(dcNormal)}
              bold blueRow />

            {/* Demand Charges Penal — excess above CD × ₹1000/kVA */}
            <ChRow
              desc="Demand Charges Penal"
              rate={`Rs ${DEMAND_CHARGE_PENAL_RS_KVA}`}
              kvaUnits={fn(addlKva, 1)}
              amount={addlKva > 0 ? rs(dcPenal) : '0.00'} />

            {/* Base Energy Charge — projected total kVAh × Normal rate (7.15 Rs/kVAh).
                TGSPDCL billing model §3: energy is charged at Normal rate on total kVAh,
                then adjusted separately for Peak penalty and Off-Peak rebate. */}
            <ChRow
              desc="Base Energy Charge"
              rate={`Ps ${Math.round(rateNormal * 100)}`}
              kvaUnits={fn(projTotalKvah, 0)}
              amount={rs(projEcBase)}
              bold blueRow />

            {/* ToD Peak Penalty — projected Peak kVAh × (Peak rate − Normal rate).
                Positive adjustment — adds to bill. Window: 06:00–10:00 + 18:00–22:00. */}
            <ChRow
              desc="ToD Peak Penalty"
              rate={`Ps ${Math.round((ratePeak - rateNormal) * 100)}`}
              kvaUnits={fn(projKvahPeak, 0)}
              amount={projPeakPenalty > 0 ? `+${rs(projPeakPenalty)}` : '0.00'} />

            {/* ToD Off-Peak Rebate — projected Off-Peak kVAh × (Normal rate − Off-Peak rate).
                Negative adjustment — saves money. Window: 22:00–06:00. */}
            <ChRow
              desc="ToD Off-Peak Rebate"
              rate={`Ps ${Math.round((rateNormal - rateOffpeak) * 100)}`}
              kvaUnits={fn(projKvahOffpeak, 0)}
              amount={projOffpeakRebate > 0 ? `-${rs(projOffpeakRebate)}` : '0.00'} />

            {/* Electricity Duty — projected full-month kVAh × 6 paise */}
            <ChRow
              desc="Electricity Duty"
              rate={`Ps ${Math.round(ELECTRICITY_DUTY_RS_KVAH * 100)}`}
              kvaUnits={fn(edKvah, 0)}
              amount={rs(electricityDutyRsProj)}
              blueRow />

            <ChRow
              desc="Colony  Charges"
              rate="Ps 730"
              kvaUnits={d.colonyChargesRs > 0 ? fn(d.colonyChargesRs / 7.30, 0) : '0'}
              amount={d.colonyChargesRs > 0 ? rs(d.colonyChargesRs) : '0.00'} />
            <ChRow
              desc="L&F  Charges"
              rate="Ps 715"
              kvaUnits={d.lfChargesRs > 0 ? fn(d.lfChargesRs / 7.15, 0) : '0'}
              amount={d.lfChargesRs > 0 ? rs(d.lfChargesRs) : '0.00'} />
            <ChRow
              desc="Green Energy Charges"
              rate="Ps 0"
              kvaUnits="0"
              amount="0.00" />
            <ChRow
              desc="FSA  Charges"
              rate="Ps"
              kvaUnits=""
              amount="0.00" />
          </View>

          {/* RIGHT: amounts summary — all projected full-month values */}
          <View style={S.rightPane}>
            <View style={S.sHdr}>
              <Text style={S.sHdrText}>Amount Summary</Text>
            </View>
            {/* Sub Total = projected EC + Demand + ED + Colony + L&F (matches dashboard Projected Bill) */}
            <AmtRow lbl="Sub Total"              val={rs(projSubTotalRs)}          bold />
            <AmtRow lbl="Customer Charges"       val={rs(customerChargesRs)}       />
            {acdSurchargeRs > 0 && (
              <AmtRow lbl="ACD Surcharge"        val={rs(acdSurchargeRs)}          />
            )}
            {/* Gross Total = Sub Total + Customer Charges + ACD (projected full-month) */}
            <AmtRow lbl="Gross Total (Projected)" val={rs(projGrossTotalRs)}       bold />
            {/* Net Bill Amount = projected net payable from billing engine */}
            <AmtRow lbl="Net Bill Amount"        val={rs(netBillAmountRs)}         bold />
            <AmtRow lbl="Total Amount Payable"   val={rs(totalAmountPayableRs)}    bold />
          </View>
        </View>

        {/* ── Cortex Notes ────────────────────────────────────────────── */}
        <View style={S.noteBox}>
          <Text style={[S.noteLine, { marginBottom: 2 }]}>
            {'* This is a CORTEX system-generated Projected Month-End Estimate for '}
            {d.billingMonth}
            {'. Generated: ' + genDateTime + '. The official TGSPDCL invoice supersedes this document for all payment purposes.'}
          </Text>
          <Text style={S.noteLine}>
            {'* Projected EC (' + d.daysPassed + '/' + d.daysInMonth + ' days): Base Rs.' + rs(projEcBase) +
             ' + Peak Penalty Rs.' + rs(projPeakPenalty) +
             ' - Off-Peak Rebate Rs.' + rs(projOffpeakRebate) +
             ' = Rs.' + rs(projTotalEnergyCharge) + ' total.'}
          </Text>
          <Text style={S.noteLine}>
            {'* Rates: Normal Rs.' + fn(rateNormal, 2) + '/kVAh · Peak +Rs.' + fn(ratePeak - rateNormal, 2) + '/kVAh · Off-Peak -Rs.' + fn(rateNormal - rateOffpeak, 2) + '/kVAh | ' +
             'Proj. Total: ' + fn(projTotalKvah, 0) + ' kVAh · Peak: ' + fn(projKvahPeak, 0) + ' kVAh · Off-Peak: ' + fn(projKvahOffpeak, 0) + ' kVAh.'}
          </Text>
          <Text style={S.noteLine}>
            {'* Demand: Rs.500/kVA x ' + fn(normalKva, 0) + ' kVA = Rs.' + rs(dcNormal) +
              (addlKva > 0 ? ' | Penal: Rs.1000/kVA x ' + fn(addlKva, 0) + ' kVA = Rs.' + rs(dcPenal) : '') +
              ' | ED: 6p/kVAh x ' + fn(projTotalKvah, 0) + ' kVAh = Rs.' + rs(electricityDutyRsProj) + ' | Customer: Rs.' + rs(customerChargesRs) + '/month.'}
          </Text>
          <Text style={S.noteLine}>
            {'* Projected Sub Total (EC + Demand + ED): Rs.' + rs(projSubTotalRs) +
             ' | Projected Gross Total (Sub Total + Customer): Rs.' + rs(projGrossTotalRs) + '.'}
          </Text>
          <Text style={S.noteLine}>
            {'* Projected Net Payable: Rs.' + rs(totalAmountPayableRs) +
             ' (linear run-rate x ' + d.daysInMonth + ' days from ' + d.daysPassed + ' days of data).'}
          </Text>
        </View>

        {/* ── Words + Net payable ──────────────────────────────────────── */}
        <View style={S.wordsBox}>
          <Text style={S.wordsLbl}>Total Amount Payable (Rs)</Text>
          <Text style={S.wordsVal}>
            {numberToWords(Math.round(totalAmountPayableRs))}
          </Text>
        </View>

        <View style={S.netBox}>
          <View>
            <Text style={S.netLbl}>Projected Net Payable (Month-End)</Text>
            <Text style={S.netSub}>
              {'[' + d.daysPassed + '/' + d.daysInMonth + ' days elapsed] · ' +
               'Projected gross: Rs.' + rs(projGrossTotalRs) + ' · Net payable: Rs.' + rs(totalAmountPayableRs)}
            </Text>
          </View>
          <Text style={S.netAmt}>{rs(totalAmountPayableRs)}</Text>
        </View>

        {/* ── Signature ───────────────────────────────────────────────── */}
        <View style={S.sigRow}>
          <Text style={S.sigLeft}>Please Quote the code no.   in all correspondence   PTO</Text>
          <View style={S.sigRight}>
            <View style={S.sigLine} />
            <Text style={S.sigText}>Senior Accounts Officer</Text>
            <Text style={S.sigSub}>TGSPDCL / CORTEX Estimate</Text>
          </View>
        </View>

      </Page>
    </Document>
  );
}

// ─── State-specific PDF document factory ─────────────────────────────────────
//
// Each entry maps a stateCode to its PDF renderer component.
// To add a new state:
//   1. Create src/components/dashboard/<StateName>BillPdfDocument.tsx
//   2. Export a default Document component from it
//   3. Import and register it here — nothing else changes.
//
// Telangana (TGSPDCL) is the fallback for unregistered/unknown state codes.

function getStatePdfDocument(data: BillPdfData): JSX.Element {
  switch (data.stateCode) {
    case 'west-bengal':
      return <WestBengalBillPdfDocument d={data} />;
    case 'telangana':
    default:
      // All other states (and the fallback) use the TGSPDCL HT format.
      return <BillPdfDocument d={data} />;
  }
}

function getStateLabel(data: BillPdfData): string {
  switch (data.stateCode) {
    case 'west-bengal': return 'WBSEDCL';
    case 'telangana':   return 'TGSPDCL';
    default:            return 'TGSPDCL';
  }
}

function getStateFilename(data: BillPdfData): string {
  const label    = getStateLabel(data);
  const month    = data.billingMonth.replace(/[\s,]+/g, '_');
  return `CORTEX_${label}_Bill_${month}.pdf`;
}

// ─── Download Button (state-aware public entry point) ────────────────────────

export function BillPdfDownloadButton({ data }: { data: BillPdfData }) {
  const filename   = getStateFilename(data);
  const stateLabel = getStateLabel(data);
  const document   = getStatePdfDocument(data);

  return (
    <PDFDownloadLink document={document} fileName={filename}>
      {({ loading }) => (
        <button
          disabled={loading}
          style={{ width: '100%' }}
          className={[
            'flex items-center justify-center gap-2 px-4 py-3',
            'rounded-xl font-semibold text-sm border-2 transition-all',
            loading
              ? 'bg-ink-100 text-ink-400 border-ink-200 cursor-wait'
              : 'bg-brand-600 text-white border-brand-600 hover:bg-brand-700 active:scale-[0.98] shadow-md',
          ].join(' ')}
        >
          {loading
            ? `Generating ${stateLabel} Bill PDF…`
            : 'Download Bill PDF  —  ' + data.billingMonth}
        </button>
      )}
    </PDFDownloadLink>
  );
}